import { jsx as _jsx } from "react/jsx-runtime";
import { resolveLabel } from "./derive.js";
import { LinesGrid } from "./blocks.js";
import { blankLine, computeCell, formulaInputs, lineColumns, linesMax, linesMin, linesOf, linesProblems } from "./lines.js";
export function LinesField({ field, value, onChange, ctx, disabled, id, checked = false, refused = {}, onCreate }) {
    const { i18n } = ctx;
    const lines = linesOf(value);
    const problems = linesProblems(field, lines, checked).cells;
    const errors = {};
    for (const [i, cells] of Object.entries(problems)) {
        for (const [key, p] of Object.entries(cells))
            (errors[Number(i)] ??= {})[key] = i18n.t(p.key, p.vars);
    }
    for (const [i, cells] of Object.entries(refused))
        for (const [key, text] of Object.entries(cells))
            (errors[Number(i)] ??= {})[key] = text;
    const decimalsOf = (c) => c.decimals ?? (c.unit === 'sar' ? 2 : c.type === 'integer' ? 0 : undefined);
    const figure = (n, decimals = 2) => n.toLocaleString(i18n.locale === 'ar' ? 'ar-u-nu-latn' : i18n.locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    const columns = lineColumns(field).map((c) => {
        const label = resolveLabel(c.label, c.label_key, i18n);
        const shape = { label, ...(c.width ? { width: c.width } : {}), ...(c.total ? { total: true } : {}) };
        const decimals = decimalsOf(c);
        if (c.type === 'computed') {
            const inputs = formulaInputs(c.of);
            const invalid = (_row, i) => inputs.some((k) => Boolean(errors[i]?.[k]));
            return {
                ...shape, key: c.key, type: 'readonly', decimals: c.decimals ?? 2,
                render: (row, i) => {
                    const n = invalid(row, i) ? null : computeCell(c, row);
                    return _jsx("span", { "data-line-computed": c.key, children: n === null ? '—' : figure(n, c.decimals ?? 2) });
                },
                sum: (rows) => (rows.some((r, i) => invalid(r, i)) ? null : rows.reduce((t, r) => t + (computeCell(c, r) ?? 0), 0)),
            };
        }
        // A figure column's total is «—» while a line in it is refused: never a sum of a value that will not be kept.
        const sum = c.total ? { sum: (rows) => (rows.some((_, i) => Boolean(errors[i]?.[c.key])) ? null : rows.reduce((t, r) => t + (Number(r[c.key]) || 0), 0)) } : {};
        if (c.type === 'number' || c.type === 'integer') {
            return { ...shape, ...sum, key: c.key, type: 'number', ...(c.type === 'integer' ? { integer: true } : {}),
                ...(decimals !== undefined ? { decimals } : {}), ...(c.unit ? { unit: i18n.t(`unit.${c.unit}`) } : {}) };
        }
        if (c.type === 'date')
            return { ...shape, key: c.key, type: 'date' };
        if (c.type === 'boolean')
            return { ...shape, key: c.key, type: 'boolean' };
        if (c.type === 'select') {
            const options = (c.options ?? []).map((o) => ({ value: o.value, label: resolveLabel(o.label, o.label_key, i18n), ...(o.group ? { detail: o.group } : {}) }));
            // A choice from a table is a lookup, as a form's select is; a short closed list of words, a select.
            if (c.options_from?.path || options.some((o) => typeof o.value !== 'string')) {
                return { ...shape, key: c.key, type: 'lookup', options: options,
                    ...(c.options_from?.create && onCreate ? { onCreate: (text, line) => onCreate(c, line, text), createLabel: (text) => i18n.t('lookup.add', { text }) } : {}) };
            }
            return { ...shape, key: c.key, type: 'select', options: options.map((o) => ({ value: String(o.value), label: o.label })) };
        }
        return { ...shape, key: c.key, type: 'text' };
    });
    return (_jsx(LinesGrid, { id: id, role: "group", ...(id ? { 'aria-labelledby': `${id}-label` } : {}), "data-lines-field": field.key, columns: columns, rows: lines, onChange: (rows) => onChange(rows), blank: () => blankLine(field), errors: errors, min: linesMin(field), max: linesMax(field), ...(disabled !== undefined ? { disabled } : {}), locale: i18n.locale, labels: {
            add: i18n.t('lines.add'), remove: i18n.t('lines.remove'), empty: i18n.t('lines.empty'),
            total: field.totals_label || field.totals_label_key ? resolveLabel(field.totals_label, field.totals_label_key, i18n) : i18n.t('lines.total'),
            full: i18n.t('lines.max', { n: linesMax(field) }), removed: (n) => i18n.t('lines.removed', { n }),
            cell: (column, n) => i18n.t('lines.cell', { column, n }),
            pasted: (lines, unread) => i18n.t(unread ? 'lines.pasted.unread' : 'lines.pasted', { lines, unread }),
        } }));
}
//# sourceMappingURL=lines-field.js.map